'use client';

import { useState, useCallback, useMemo, Suspense } from 'react';
import Link from 'next/link';
import { ECHOPAY_SVG } from '@/assets/svgs';
import { useRouter, useSearchParams } from 'next/navigation';
import InputField from '@/components/InputField';
import Button from '@/components/ReusableButton';
import { useToast } from '@/components/ToastProvider';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { setAuthCookies } from '@/app/actions/auth.actions';
import { ROUTES } from '@/lib/config/routes.config';
import { authApi } from '@/lib/api';
import { kycApi } from '@/lib/api/kyc';
import { logger } from '@/utils/logger';

function LoginContent() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { loginAsync, isLoggingIn } = useAuth();
  const [isRedirecting, setIsRedirecting ] = useState(false);

  const searchParams = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const isButtonDisabled = useMemo(() => {
    return !formData.email.trim() || !formData.password.trim();
  }, [formData.email, formData.password]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

const onLogin = useCallback(
  async (e: React.FormEvent) => {
    e.preventDefault();
    setIsRedirecting(true);
    try {
      // Step 1: Login and get token
      const loginResponse = await loginAsync({
        email: formData.email,
        password: formData.password,
      });

      const accessToken = loginResponse.data.data.access_token;

      // Step 2: Set cookies with token first
      await setAuthCookies({
        accessToken
      });

      // Step 3: Fetch user and KYC data
      const [userResponse, kycResponse] = await Promise.all([
        authApi.getCurrentUser(),
        kycApi.getKycStatus(),
      ]);

      // Step 4: Determine verification status
      const emailVerified = !!userResponse.data.data?.email_verified_at;
      const hasBvnVerified = kycResponse.data.data?.verifications?.bvn?.verified ?? false;
      const isKycApproved = kycResponse.data.data?.status === 'approved';
      const hasKyc = kycResponse.data.data?.hasKyc ?? false;
      const kycVerified = hasBvnVerified && isKycApproved && hasKyc;

      // Step 5: Update cookies with correct status
      await setAuthCookies({
        accessToken,
        emailVerified,
        kycVerified,
      });

      localStorage.setItem('is_authenticated', 'true');
      queryClient.invalidateQueries({ queryKey: ['user'] });
      queryClient.invalidateQueries({ queryKey: ['kyc-status'] });

      // Step 6: Determine redirect
      let redirectUrl = ROUTES.KYC_PROTECTED.ANALYTICS as string;
      if (!emailVerified) {
        redirectUrl = ROUTES.REGISTRATION.VERIFY_EMAIL;
      } else if (!kycVerified) {
        redirectUrl = ROUTES.AUTH_PROTECTED.COMPLETE_PROFILE;
      } else if (returnUrl) {
        redirectUrl = decodeURIComponent(returnUrl);
      }

      showSuccess('Login successful', 'Welcome back!', 2000);
      router.replace(redirectUrl);
    //   router.refresh();
    } catch (error: any) {
      logger.error('Login failed:', error);
      showError(
        'Login failed',
        error?.response?.data?.message || 'Invalid credentials',
        5000
      );
      setIsRedirecting(false);
    }
  },
  [formData, loginAsync, queryClient, router, showSuccess, showError, returnUrl]
);

  return (
    <div className="min-h-screen bg-[#f8f8f8] flex mx-auto">
      {/* Left Side - Branding */}
      <div className="border-[6px] sm:border-[8px] border-[#009dff43] hidden z-50 w-full rounded-[20px] sm:rounded-[24px] relative lg:block m-3 sm:m-4 h-auto">
        <div className="bg-[url('/auth_bg.svg')] rounded-[14px] sm:rounded-[17px] w-full bg-cover h-full">
          <div className="p-4 sm:p-5 md:p-6 pb-28 sm:pb-32 md:pb-36 flex flex-col justify-between h-full">
            <Link href="/">
              <div>
                {ECHOPAY_SVG().resolvaOne({
                  className:
                    'w-[120px] h-[28px] sm:w-[130px] sm:h-[30px] md:w-[144.25px] md:h-[33px]',
                })}
              </div>
            </Link>

            <div className="bg-[#009dff1b] border-[6px] sm:border-[8px] border-[#009DFF54] rounded-[16px] sm:rounded-[20px] p-3 sm:p-4">
              <h3 className="text-[#373737] font-medium text-[18px] sm:text-[20px] md:text-[22px] xl:text-[25px] 2xl:text-[28px] leading-[28px] sm:leading-[32px] md:leading-[37px] tracking-[-0.9px] font-inter mb-2 sm:mb-2.5 md:mb-3">
                Send Crypto. <br /> Receive Cash. Instantly.
              </h3>
              <p className="font-inter font-normal text-[14px] sm:text-[15px] md:text-[16px] 2xl:text-[17px] tracking-[-0.5px] text-[#373737] leading-relaxed">
                Family gets bank alerts. Shop customers scan and pay. You and
                your business get instant fiat settlements with Resolva.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full flex items-center justify-center py-[4rem] sm:py-[5rem] px-4 sm:px-8 md:px-12 lg:p-12 relative h-auto">
        <div className="block lg:hidden absolute top-0 left-0 right-0 h-3 sm:h-4 bg-[#0046A7]"></div>
        <div className="w-full max-w-md lg:max-w-lg">
          <Link href="/">
            <div className="lg:hidden mb-[3rem] sm:mb-[4rem] flex justify-center">
              {ECHOPAY_SVG().resolvaOne({
                className: 'w-[130px] h-[30px] sm:w-[144px] sm:h-[33px]',
              })}
            </div>
          </Link>
          <div className="mb-6 sm:mb-7 md:mb-8">
            <h2 className="text-[20px] sm:text-[22px] md:text-[26px] lg:text-[28px] xl:text-[32px] 2xl:text-[36px] leading-[28px] sm:leading-[32px] md:leading-[38px] lg:leading-[44px] tracking-[-2px] font-semibold text-[#010721] mb-1.5 sm:mb-2 font-inter">
              Sign in
            </h2>
            <p className="text-[#010721] text-[12px] sm:text-[13px] md:text-[14px] font-roboto leading-[18px] sm:leading-[19px] md:leading-[20px] tracking-[0.1px] font-normal">
              Welcome back! Enter your details below.
            </p>
          </div>

          <form onSubmit={onLogin}>
            <div className="space-y-6 sm:space-y-7 md:space-y-8 lg:space-y-9">
              <InputField
                id="email"
                name="email"
                type="email"
                label="Email Address"
                placeholder="email@example.com"
                value={formData.email}
                onChange={handleChange}
              />

              <InputField
                id="password"
                name="password"
                label="Password"
                placeholder="****************"
                value={formData.password}
                onChange={handleChange}
                showPasswordToggle={true}
              />
            </div>

            <Link
              href="/forgot-password"
              className="font-roboto font-medium text-sm sm:text-[15px] md:text-base leading-[0.5px] underline text-[#0046A7] mt-5 sm:mt-6 md:mt-7 mb-8 sm:mb-10 md:mb-12 p-0 block"
            >
              Forgot Password?
            </Link>

            <Button
              type="submit"
              disabled={isButtonDisabled}
              fullWidth
              className="mb-8 sm:mb-9 md:mb-10 h-11 sm:h-12 md:h-13 lg:h-14 text-xs sm:text-[13px] md:text-sm lg:text-base"
              isLoading={isLoggingIn || isRedirecting}
            >
              Continue
            </Button>

            <p className="text-center text-[#010721] text-[13px] sm:text-[14px] md:text-[15px] lg:text-[16px] font-roboto">
              Do not have an account?{' '}
              <Link
                href="/register"
                className="text-[#0046A7] font-medium underline hover:text-[#0046a7]"
              >
                Create Your Account.
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function LoginFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8]">
      <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#0046A7] border-t-transparent" />
    </div>
  );
}

export default function LoginUI() {
  return (
    <Suspense fallback={<LoginFallback />}>
      <LoginContent />
    </Suspense>
  );
}
